// A tune link that carries its SETTINGS (Sep 2026, for the Learn pages: "pick
// a tune, apply some settings, then link to the example"). `?tune=` says what
// to open; a recipe says how to look at it — the surface and lens, which
// diagrams, the analysis lane, the reharm levers with the switch on, the
// voice-leading engine and family, the backing instrument, the bass and comp
// styles, a whole style preset, the swing, how loose the band plays and how the
// melody is phrased. Applied ONCE on arrival, after the tune has
// landed, then STRIPPED from the address bar: a recipe is a starting point,
// not a lock — the reader who then changes a lever must not have the link
// snap it back on every refresh, and the address bar goes on reflecting the
// tune (and the reading edition) as it always did. Everything here is pure
// except `applyTuneRecipe`, which is the store's headless command-layer
// pattern (useStore.getState()), so a recipe can be applied from a test.

import { flagEnabled } from '@/auth/flags';
import { COMP_STYLES, type CompStyle } from '@/music/comping';
import { ADVENTURE_BANDS, type AdventureBand } from '@/music/reharm';
import type { ReharmRung } from '@/music/reharm/types';
import { STYLE_PRESETS, type StyleId } from '@/music/style';
import { SWING_CHOICE_IDS, swingPatch, type SwingChoice } from '@/music/swingFeel';
import { MELODY_PHRASINGS, type MelodyPhrasing } from '@/music/bandFeel';
import { FEEL_TIMINGS, type FeelTiming } from '@/audio/feel';
import { VOICE_LEADING_ENGINES, type VoiceLeadingEngineId } from '@/music/voiceLeadingEngines';
import { VOICING_FAMILY_SELECTION_IDS, type VoicingFamilySelection } from '@/music/voicingFamilies';
import { BASS_STYLES, type BassStyle } from '@/music/walkingBass';
import { APP_ROUTES } from '@/navigation';
import { useStore } from '@/state/store';

export interface TuneRecipe {
  view?: 'chart' | 'score' | 'map';
  lens?: 'none' | 'comping' | 'melody';
  chords?: 'guitar' | 'piano';
  analysis?: boolean;
  /** The Reharmonise switch ON at these levers. */
  reharm?: { maxRung: ReharmRung; adventure: AdventureBand; density: number };
  engine?: VoiceLeadingEngineId;
  family?: VoicingFamilySelection;
  /** Which instrument the comp channel PLAYS. */
  backing?: 'guitar' | 'piano' | 'piano-left';
  bass?: BassStyle;
  comp?: CompStyle;
  /** A whole style preset, applied before the individual channel fields. */
  style?: StyleId;
  /** The swing: straight, with the kit, or a fixed feel (swingFeel.ts). */
  swing?: SwingChoice;
  /** How loose the band plays: tight, relaxed or loose. */
  feel?: FeelTiming;
  /** How the melody is phrased where the band swings. */
  phrasing?: MelodyPhrasing;
}

/** The query keys a recipe uses — the ones stripped after it is applied. */
export const RECIPE_KEYS = [
  'view',
  'lens',
  'chords',
  'analysis',
  'reharm',
  'engine',
  'family',
  'backing',
  'bass',
  'comp',
  'style',
  'swing',
  'feel',
  'phrasing',
] as const;

const oneOf = <T extends string>(value: string | null, allowed: readonly T[]): T | undefined =>
  value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : undefined;

/** `reharm=5:stretched:0.5` — rung, band, density; the band and density may be
 *  left off (`reharm=5`, `reharm=5:out`). Anything unreadable is ignored. */
function parseReharm(value: string | null): TuneRecipe['reharm'] | undefined {
  if (value === null) return undefined;
  const [rungText, bandText, densityText] = value.split(':');
  const rung = Number(rungText);
  if (!Number.isInteger(rung) || rung < 0 || rung > 8) return undefined;
  const adventure = oneOf(bandText ?? 'inside', ADVENTURE_BANDS) ?? 'inside';
  const densityRaw = densityText === undefined ? 0.5 : Number(densityText);
  const density = Number.isFinite(densityRaw) ? Math.max(0.1, Math.min(1, densityRaw)) : 0.5;
  return { maxRung: rung as ReharmRung, adventure, density };
}

export function parseTuneRecipe(params: URLSearchParams): TuneRecipe {
  const recipe: TuneRecipe = {};
  const view = oneOf(params.get('view'), ['chart', 'score', 'map'] as const);
  if (view) recipe.view = view;
  const lens = oneOf(params.get('lens'), ['none', 'comping', 'melody'] as const);
  if (lens) recipe.lens = lens;
  const chords = oneOf(params.get('chords'), ['guitar', 'piano'] as const);
  if (chords) recipe.chords = chords;
  const analysis = params.get('analysis');
  if (analysis !== null) recipe.analysis = analysis !== '0' && analysis !== 'off';
  const reharm = parseReharm(params.get('reharm'));
  if (reharm) recipe.reharm = reharm;
  const engine = oneOf(
    params.get('engine'),
    VOICE_LEADING_ENGINES.map((e) => e.id),
  );
  if (engine) recipe.engine = engine;
  const family = oneOf(params.get('family'), VOICING_FAMILY_SELECTION_IDS);
  if (family) recipe.family = family;
  const backing = oneOf(params.get('backing'), ['guitar', 'piano', 'piano-left'] as const);
  if (backing) recipe.backing = backing;
  const bass = oneOf(
    params.get('bass'),
    BASS_STYLES.map((s) => s.id),
  );
  if (bass) recipe.bass = bass;
  const comp = oneOf(
    params.get('comp'),
    COMP_STYLES.map((s) => s.id),
  );
  if (comp) recipe.comp = comp;
  const style = oneOf(
    params.get('style'),
    STYLE_PRESETS.map((p) => p.id),
  );
  if (style) recipe.style = style;
  const swing = oneOf(params.get('swing'), SWING_CHOICE_IDS);
  if (swing) recipe.swing = swing;
  const feel = oneOf(params.get('feel'), FEEL_TIMINGS);
  if (feel) recipe.feel = feel;
  const phrasing = oneOf(params.get('phrasing'), MELODY_PHRASINGS);
  if (phrasing) recipe.phrasing = phrasing;
  return recipe;
}

export const hasRecipe = (recipe: TuneRecipe): boolean => Object.keys(recipe).length > 0;

/** The recipe as query parameters, the inverse of `parseTuneRecipe`. */
export function recipeParams(recipe: TuneRecipe): URLSearchParams {
  const params = new URLSearchParams();
  if (recipe.view) params.set('view', recipe.view);
  if (recipe.lens) params.set('lens', recipe.lens);
  if (recipe.chords) params.set('chords', recipe.chords);
  if (recipe.analysis !== undefined) params.set('analysis', recipe.analysis ? '1' : '0');
  if (recipe.reharm) {
    params.set(
      'reharm',
      `${recipe.reharm.maxRung}:${recipe.reharm.adventure}:${recipe.reharm.density}`,
    );
  }
  if (recipe.engine) params.set('engine', recipe.engine);
  if (recipe.family) params.set('family', recipe.family);
  if (recipe.backing) params.set('backing', recipe.backing);
  if (recipe.bass) params.set('bass', recipe.bass);
  if (recipe.comp) params.set('comp', recipe.comp);
  if (recipe.style) params.set('style', recipe.style);
  if (recipe.swing) params.set('swing', recipe.swing);
  if (recipe.feel) params.set('feel', recipe.feel);
  if (recipe.phrasing) params.set('phrasing', recipe.phrasing);
  return params;
}

/** A workbench link to a tune with a recipe — what a Learn page prints. */
export function recipeUrl(
  tune: { title: string; composer?: string; source?: 'realbook'; read?: 'bb' | 'eb' | 'c-bass' },
  recipe: TuneRecipe = {},
): string {
  const params = recipeParams(recipe);
  params.set('tune', tune.title);
  if (tune.composer) params.set('by', tune.composer);
  if (tune.source) params.set('source', tune.source);
  // The reading edition is the existing `read` param (useTuneUrl), not a
  // recipe key: it is reflected, never stripped.
  if (tune.read) params.set('read', tune.read);
  // `tune` first, so the link reads as "this tune, then how".
  const ordered = new URLSearchParams();
  for (const key of ['tune', 'by', 'source', 'read']) {
    const v = params.get(key);
    if (v !== null) ordered.set(key, v);
  }
  params.forEach((v, k) => {
    if (!ordered.has(k)) ordered.set(k, v);
  });
  return `${APP_ROUTES.workbench}?${ordered.toString()}`;
}

/** Apply a recipe to the loaded tune. Order matters in one place: a style
 *  preset first, so the bass and comp fields named beside it win over it. */
export function applyTuneRecipe(recipe: TuneRecipe): void {
  const s = useStore.getState();
  if (recipe.style) s.applyStyle(recipe.style);
  const band: Parameters<typeof s.patchBand>[0] = {};
  if (recipe.backing) band.compInstrument = recipe.backing;
  if (recipe.bass) band.bassStyle = recipe.bass;
  if (recipe.comp) band.compStyle = recipe.comp;
  if (recipe.swing) Object.assign(band, swingPatch(recipe.swing));
  if (recipe.phrasing) band.melodyPhrasing = recipe.phrasing;
  if (Object.keys(band).length > 0) s.patchBand(band);
  if (recipe.feel) s.setFeelTiming(recipe.feel);
  if (recipe.view) s.setViewSurface(recipe.view);
  if (recipe.lens) s.setVoicingLens(recipe.lens);
  if (recipe.chords) s.setDiagramInstrument(recipe.chords);
  if (recipe.analysis !== undefined) s.setShowAnalysis(recipe.analysis);
  if (recipe.engine) s.setVoiceLeadingEngine(recipe.engine);
  if (recipe.family) s.setVoicingFamily(recipe.family);
  // The reharm flag gates the ENGINE (#327), so on an account with it off the
  // link applies everything else and leaves the harmony alone: the preview
  // would be a layer written by the engine, behind a switch the flag hides.
  if (recipe.reharm && flagEnabled('reharm')) {
    s.setReharmSettings(recipe.reharm);
    useStore.getState().previewReharmPassage(useStore.getState().reharmSettings);
  }
}

/** The same params with the recipe taken out. */
export function withoutRecipe(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of RECIPE_KEYS) next.delete(key);
  return next;
}
